"use client";
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useKrpano } from '@/contexts/KrpanoProvider';
import { useIdPath } from "@/contexts/IdPathProvider";
import listService from '@/templates/Admin/variants/default/utils/services/list';
import { normalizeHotspotConfig, buildTitleCss, convertApiColorToHex, convertHexToRgba } from './HotspotManager';

// API InfoList interface based on API response
export interface APIInfoList {
    id: number;
    type: string;
    style: string;
    scene_id: number;
    ath: string | number;
    atv: string | number;
    scale: string | number;
    display_type: 'both' | 'icon-only';
    config: string | Record<string, any>;
    config_list: string | Record<string, any>;
    created_by: number;
    updated_by: number;
    created_at: string;
    updated_at: string;
    list_title?: string;
    list_description?: string;
    scene_name?: string;
    translations?: Array<{
        language_id: string;
        title?: string;
        description?: string;
        entity_type: string;
    }>;
    items?: Array<{
        id: number;
        sort_order: number;
        media_path: string;
        media_type: string;
        action_type: string;
        action_data: any;
        translations?: Array<{
            language_id: string;
            name?: string;
            entity_type: string;
        }>;
    }>;
}

interface InfoListManagerContextType {
    infoLists: APIInfoList[];
    currentSceneInfoLists: APIInfoList[];
    isLoading: boolean;
    fetchLists: (sceneName: string) => Promise<void>;
    addInfoListToScene: (infoList: APIInfoList) => void;
    removeInfoListFromScene: (infoListId: number) => void;
    updateInfoListInKrpano: (infoList: APIInfoList) => void;
    refreshInfoLists: () => Promise<void>;
    // Popup state
    popupInfoList: APIInfoList | null;
    popupPosition: { x: number; y: number } | null;
    isPopupVisible: boolean;
    showPopup: (infoList: APIInfoList, position: { x: number; y: number }) => void;
    hidePopup: () => void;
    // Update form state
    openUpdateForm: (infoList: APIInfoList) => void;
    updateFormInfoList: APIInfoList | null;
    isUpdateFormOpen: boolean;
    closeUpdateForm: () => void;
}

const InfoListManagerContext = createContext<InfoListManagerContextType | undefined>(undefined);

export const InfoListManagerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { krpano, krpanoCurrent } = useKrpano();
    const { idPath } = useIdPath();
    const [infoLists, setInfoLists] = useState<APIInfoList[]>([]);
    const [currentSceneInfoLists, setCurrentSceneInfoLists] = useState<APIInfoList[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [lastScene, setLastScene] = useState<string>('');

    // Popup state
    const [popupInfoList, setPopupInfoList] = useState<APIInfoList | null>(null);
    const [popupPosition, setPopupPosition] = useState<{ x: number; y: number } | null>(null);
    const [isPopupVisible, setIsPopupVisible] = useState(false);

    // Update form state
    const [updateFormInfoList, setUpdateFormInfoList] = useState<APIInfoList | null>(null);
    const [isUpdateFormOpen, setIsUpdateFormOpen] = useState(false);

    // Parse config from API response
    const parseConfig = (rawConfig: any): Record<string, any> => {
        if (!rawConfig) return {};
        if (typeof rawConfig === 'string') {
            try {
                return JSON.parse(rawConfig);
            } catch (error) {
                console.warn('Failed to parse infoList config:', error);
                return {};
            }
        }
        return rawConfig;
    };

    // Get display name from translations or default
    const getDisplayName = (infoList: APIInfoList, langId: string = 'vn'): string => {
        // Try to get from list_label translation first
        const labelTrans = infoList.translations?.find(
            t => t.entity_type === 'list_label' && (t.language_id === langId || t.language_id === 'vi')
        );
        if (labelTrans?.title) return labelTrans.title;

        // Fallback to list title
        const listTrans = infoList.translations?.find(
            t => t.entity_type === 'list' && (t.language_id === langId || t.language_id === 'vi')
        );
        if (listTrans?.title) return listTrans.title;

        // Final fallback
        return infoList.list_title || 'Info List';
    };

    // Fetch lists by scene name
    const fetchLists = useCallback(async (sceneName: string) => {
        if (!sceneName || !idPath) return;

        setIsLoading(true);
        try {
            const response = await listService.getAllLists(idPath, undefined, sceneName) as any;

            let listData: APIInfoList[] = [];

            // Handle different response structures
            if (response?.success && response?.data) {
                listData = Array.isArray(response.data) ? response.data : [];
            } else if (Array.isArray(response)) {
                listData = response;
            }

            // Filter for info_list type only
            listData = listData.filter(item => item.type === 'info_list');

            setInfoLists(listData);
            setCurrentSceneInfoLists(listData);

        } catch (error) {
            console.error('Error fetching info lists:', error);
            setInfoLists([]);
            setCurrentSceneInfoLists([]);
        } finally {
            setIsLoading(false);
        }
    }, [idPath]);

    // Robust scale setter (similar to HotspotManager)
    const setInfoListScale = useCallback((hotspotName: string, value?: number) => {
        if (!krpano || value === undefined || Number.isNaN(value)) return;
        try {
            krpano.call(`delayedcall(0.02, set(hotspot[${hotspotName}].scale, ${value}); tween(hotspot[${hotspotName}].scale, ${value}, 0.0, default, 0.1)); delayedcall(0.6, set(hotspot[${hotspotName}].scale, ${value}));`);
        } catch (err) {
            try { krpano.set(`hotspot[${hotspotName}].scale`, value); } catch (e) { /* noop */ }
            setTimeout(() => { try { krpano.set(`hotspot[${hotspotName}].scale`, value); } catch (e) { /* noop */ } }, 700);
        }
    }, [krpano]);

    // Add info lists for current scene to krpano
    const addInfoListsForCurrentScene = useCallback((currentSceneName: string) => {
        if (!krpano || !Array.isArray(infoLists) || infoLists.length === 0) {
            return;
        }

        // Remove existing info list hotspots first
        try {
            const hotspotCount = Number(krpano.get("hotspot.count") || 0);
            if (hotspotCount > 0) {
                for (let i = 0; i < hotspotCount; i++) {
                    try {
                        const hotspotName = String(krpano.get(`hotspot[${i}].name`) || '');
                        // Remove only info list hotspots (prefix: infolist_)
                        if (hotspotName && hotspotName.startsWith('infolist_')) {
                            krpano.call(`removehotspot(${hotspotName})`);
                        }
                    } catch (e) { /* ignore */ }
                }
            }
        } catch (error) {
            console.warn('Error removing info list hotspots:', error);
        }

        // Add each info list as a hotspot
        infoLists.forEach((infoList) => {
            // Skip the one being edited to avoid duplicates (infoList component shows its own preview)
            if (isUpdateFormOpen && updateFormInfoList?.id === infoList.id) {
                return;
            }

            const hotspotName = `infolist_${infoList.id}`;
            const ath = parseFloat(String(infoList.ath));
            const atv = parseFloat(String(infoList.atv));
            const scale = parseFloat(String(infoList.scale)) || 0.3;
            const config = parseConfig(infoList.config);
            const normalizedConfig = normalizeHotspotConfig(config);

            try {
                // Add main hotspot
                krpano.call(`addhotspot(${hotspotName})`);

                // Determine style name
                const sanitizedStyle = String(infoList.style || 'default').replace(/[^a-zA-Z0-9_-]/g, '');
                let styleName = sanitizedStyle || 'default';
                if (styleName && !styleName.startsWith('hotspot_') && !styleName.endsWith('_style')) {
                    styleName = `hotspot_${styleName}_style`;
                }

                // Load style
                try {
                    krpano.call(`hotspot[${hotspotName}].loadstyle(${styleName})`);
                } catch (styleError) {
                    console.warn(`Failed to load style ${styleName}, using fallback`);
                    krpano.call(`hotspot[${hotspotName}].loadstyle(hotspot_default_style)`);
                }

                // Set position
                krpano.set(`hotspot[${hotspotName}].ath`, ath);
                krpano.set(`hotspot[${hotspotName}].atv`, atv);

                // Set distorted to false to keep fixed size when zooming
                krpano.set(`hotspot[${hotspotName}].distorted`, false);
                krpano.set(`hotspot[${hotspotName}].zoom`, false);

                // Set scale with robust setter
                setInfoListScale(hotspotName, scale);

                // Set z-order and visibility
                krpano.set(`hotspot[${hotspotName}].zorder`, 6);
                krpano.set(`hotspot[${hotspotName}].visible`, true);
                krpano.set(`hotspot[${hotspotName}].enabled`, true);
                krpano.set(`hotspot[${hotspotName}].handcursor`, false);
                krpano.set(`hotspot[${hotspotName}].capture`, true);

                // Set onclick to show popup
                krpano.set(`hotspot[${hotspotName}].onclick`, `js(window.showInfoListPopup('${infoList.id}', get(mouse.stagex), get(mouse.stagey)))`);

                // Set drag functionality ONLY if this specific list is being edited
                const isCurrentBeingEdited = isUpdateFormOpen && updateFormInfoList?.id === infoList.id;
                if (isCurrentBeingEdited) {
                    krpano.set(`hotspot[${hotspotName}].ondown`, "set(start_ath, get(ath)); set(start_atv, get(atv)); set(dragging,true); draghotspot();");
                    krpano.set(`hotspot[${hotspotName}].onup`, `
                        set(dragging,false); 
                        js(window.updateInfoListPosition('${hotspotName}'))
                    `);
                } else {
                    // Not in edit mode, disable dragging
                    krpano.set(`hotspot[${hotspotName}].ondown`, "");
                    krpano.set(`hotspot[${hotspotName}].onup`, "");
                }

                // Add text label if display_type is 'both'
                if (infoList.display_type === 'both') {
                    const textHotspotName = `${hotspotName}_text`;
                    const displayText = getDisplayName(infoList);

                    try {
                        krpano.call(`addhotspot(${textHotspotName})`);
                        krpano.set(`hotspot[${textHotspotName}].url`, '%SWFPATH%/plugins/textfield.swf');
                        krpano.set(`hotspot[${textHotspotName}].html`, displayText);
                        krpano.set(`hotspot[${textHotspotName}].ath`, ath);
                        krpano.set(`hotspot[${textHotspotName}].atv`, atv);
                        krpano.set(`hotspot[${textHotspotName}].oy`, 10);
                        krpano.set(`hotspot[${textHotspotName}].edge`, 'top');

                        // Set distorted to false to keep label size fixed
                        krpano.set(`hotspot[${textHotspotName}].distorted`, false);
                        krpano.set(`hotspot[${textHotspotName}].zoom`, false);

                        krpano.set(`hotspot[${textHotspotName}].zorder`, 7);
                        krpano.set(`hotspot[${textHotspotName}].visible`, true);
                        krpano.set(`hotspot[${textHotspotName}].enabled`, false);
                        krpano.set(`hotspot[${textHotspotName}].vcenter`, true);
                        krpano.set(`hotspot[${textHotspotName}].textalign`, 'center');

                        // Build and apply CSS from config
                        const fakeHotspot = { title: displayText };
                        const titleCss = buildTitleCss(fakeHotspot as any, normalizedConfig);
                        krpano.set(`hotspot[${textHotspotName}].css`, titleCss);

                        // Apply advanced background and border settings
                        const titleConfig = normalizedConfig?.Title || {};

                        // Handle auto-width/wordwrap
                        const resolvedWidth = titleConfig.width || 'auto';
                        if (resolvedWidth !== 'auto') {
                            krpano.set(`hotspot[${textHotspotName}].width`, parseInt(String(resolvedWidth).replace('px', ''), 10));
                            krpano.set(`hotspot[${textHotspotName}].wordwrap`, true);
                        }

                        // Background
                        const backgroundColor = titleConfig['background-color'] || '#FFFFFF';
                        const bgColorLower = String(backgroundColor).toLowerCase();
                        const isTransparent = bgColorLower === 'transparent' || bgColorLower === 'none' ||
                            (bgColorLower.startsWith('rgba') && bgColorLower.match(/,\s*0\s*\)$/));
                        const hasBackground = !isTransparent && backgroundColor;

                        krpano.set(`hotspot[${textHotspotName}].background`, hasBackground ? 'true' : 'false');
                        if (hasBackground) {
                            const bgOpacity = titleConfig['background-opacity'];
                            krpano.set(`hotspot[${textHotspotName}].backgroundalpha`, bgOpacity !== undefined ? bgOpacity.toString() : '0.9');

                            const hexColor = convertApiColorToHex(String(backgroundColor));
                            if (hexColor && typeof hexColor === 'string' && hexColor.startsWith('#')) {
                                krpano.set(`hotspot[${textHotspotName}].backgroundcolor`, `0x${hexColor.slice(1)}`);
                            }
                        }

                        // Border radius
                        const borderRadius = titleConfig['border-radius'];
                        if (borderRadius) {
                            const radiusValue = parseInt(String(borderRadius).replace(/[%px]/g, ''), 10);
                            if (!isNaN(radiusValue) && radiusValue > 0) {
                                krpano.set(`hotspot[${textHotspotName}].bgroundedge`, radiusValue);
                            }
                        }

                        // Border
                        const borderValue = titleConfig.border || '';
                        const borderWidthMatch = String(borderValue).match(/([\d.]+)\s*px/);
                        const borderWidthVal = borderWidthMatch ? parseFloat(borderWidthMatch[1]) : 0;
                        if (borderWidthVal > 0) {
                            krpano.set(`hotspot[${textHotspotName}].border`, 'true');
                            krpano.set(`hotspot[${textHotspotName}].borderwidth`, borderWidthVal);

                            // Try to extract border color
                            const borderHexMatch = String(borderValue).match(/#([0-9a-fA-F]{3,6})/);
                            if (borderHexMatch) {
                                krpano.set(`hotspot[${textHotspotName}].bordercolor`, `0x${borderHexMatch[1]}`);
                            }
                        }

                    } catch (textError) {
                        console.warn(`Error creating text label for info list ${infoList.id}:`, textError);
                    }
                }

            } catch (error) {
                console.error(`Error adding info list ${hotspotName}:`, error);
            }
        });

    }, [krpano, infoLists, setInfoListScale, isUpdateFormOpen, updateFormInfoList]);

    // Monitor scene changes and load info lists
    useEffect(() => {
        if (!krpano) return;

        const getCurrentSceneName = () => {
            return krpanoCurrent?.scene_name || (krpano.get("xml.scene") as string) || '';
        };

        const currentSceneName = getCurrentSceneName();

        if (currentSceneName && currentSceneName !== lastScene) {
            setLastScene(currentSceneName);
            fetchLists(currentSceneName);
        }

        // Poll for scene changes
        const interval = setInterval(() => {
            const newSceneName = getCurrentSceneName();
            if (newSceneName && newSceneName !== lastScene) {
                setLastScene(newSceneName);
                fetchLists(newSceneName);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [krpano, krpanoCurrent?.scene_name, lastScene, fetchLists]);

    // Add info lists to krpano when list changes
    useEffect(() => {
        if (!krpano || !Array.isArray(infoLists) || infoLists.length === 0) {
            return;
        }

        const currentSceneName = krpanoCurrent?.scene_name || (krpano.get("xml.scene") as string) || '';
        if (currentSceneName) {
            addInfoListsForCurrentScene(currentSceneName);
        }
    }, [krpano, infoLists, addInfoListsForCurrentScene, isUpdateFormOpen, updateFormInfoList]);

    // Add info list to scene (called after create)
    const addInfoListToScene = useCallback((infoList: APIInfoList) => {
        setInfoLists(prev => {
            const current = Array.isArray(prev) ? prev : [];
            return [...current, infoList];
        });
        setCurrentSceneInfoLists(prev => {
            const current = Array.isArray(prev) ? prev : [];
            return [...current, infoList];
        });
    }, []);

    // Remove info list from scene
    const removeInfoListFromScene = useCallback((infoListId: number) => {
        // Remove from krpano
        if (krpano) {
            const hotspotName = `infolist_${infoListId}`;
            const textHotspotName = `${hotspotName}_text`;
            try {
                krpano.call(`removehotspot(${hotspotName})`);
                krpano.call(`removehotspot(${textHotspotName})`);
            } catch (error) {
                console.warn(`Error removing info list ${hotspotName} from krpano:`, error);
            }
        }

        // Remove from state
        setInfoLists(prev => prev.filter(item => item.id !== infoListId));
        setCurrentSceneInfoLists(prev => prev.filter(item => item.id !== infoListId));
    }, [krpano]);

    // Update info list in krpano
    const updateInfoListInKrpano = useCallback((infoList: APIInfoList) => {
        if (!krpano) return;

        const hotspotName = `infolist_${infoList.id}`;
        const ath = parseFloat(String(infoList.ath));
        const atv = parseFloat(String(infoList.atv));
        const scale = parseFloat(String(infoList.scale)) || 0.3;

        try {
            krpano.set(`hotspot[${hotspotName}].ath`, ath);
            krpano.set(`hotspot[${hotspotName}].atv`, atv);
            setInfoListScale(hotspotName, scale);

            // Update text label position if exists
            const textHotspotName = `${hotspotName}_text`;
            try {
                krpano.set(`hotspot[${textHotspotName}].ath`, ath);
                krpano.set(`hotspot[${textHotspotName}].atv`, atv);
            } catch (e) { /* text hotspot might not exist */ }

        } catch (error) {
            console.error(`Error updating info list ${hotspotName} in krpano:`, error);
        }
    }, [krpano, setInfoListScale]);

    // Refresh info lists
    const refreshInfoLists = useCallback(async () => {
        const currentSceneName = krpanoCurrent?.scene_name || (krpano && krpano.get("xml.scene") as string) || '';
        if (currentSceneName) {
            await fetchLists(currentSceneName);

            // Re-add info lists after refresh
            if (krpano) {
                setTimeout(() => {
                    addInfoListsForCurrentScene(currentSceneName);
                }, 500);
            }
        }
    }, [krpano, krpanoCurrent?.scene_name, fetchLists, addInfoListsForCurrentScene]);

    // Popup management
    const showPopup = useCallback((infoList: APIInfoList, position: { x: number; y: number }) => {
        setPopupInfoList(infoList);
        setPopupPosition(position);
        setIsPopupVisible(true);
    }, []);

    const hidePopup = useCallback(() => {
        setPopupInfoList(null);
        setPopupPosition(null);
        setIsPopupVisible(false);
    }, []);

    // Update form management
    const openUpdateForm = useCallback((infoList: APIInfoList) => {
        setUpdateFormInfoList(infoList);
        setIsUpdateFormOpen(true);
        hidePopup();
    }, [hidePopup]);

    const closeUpdateForm = useCallback(() => {
        setUpdateFormInfoList(null);
        setIsUpdateFormOpen(false);
    }, []);

    // Adjust popup position to stay within viewport
    const adjustPopupPosition = (x: number, y: number) => {
        const popupWidth = 220;
        const popupHeight = 150;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const offset = 20;

        let adjustedX = x + offset;
        let adjustedY = y;

        if (adjustedX + popupWidth > viewportWidth - 10) {
            adjustedX = x - popupWidth - offset;
        }

        if (adjustedX < 10) {
            adjustedX = 10;
        }

        if (adjustedY - popupHeight / 2 < 10) {
            adjustedY = 10 + popupHeight / 2;
        } else if (adjustedY + popupHeight / 2 > viewportHeight - 10) {
            adjustedY = viewportHeight - 10 - popupHeight / 2;
        }

        return { x: adjustedX, y: adjustedY };
    };

    // Register global window functions for krpano callbacks
    useEffect(() => {
        (window as any).showInfoListPopup = (infoListId: string, x: number, y: number) => {
            const foundList = infoLists.find(item => item.id.toString() === infoListId);

            if (foundList) {
                const krpanoElement = document.getElementById('krpanoSWFObject') ||
                    document.querySelector('#pano') ||
                    document.querySelector('.krpano-container') ||
                    document.querySelector('div[id*="krpano"]');

                let adjustedX = x;
                let adjustedY = y;

                if (krpanoElement) {
                    const rect = krpanoElement.getBoundingClientRect();
                    adjustedX = rect.left + x;
                    adjustedY = rect.top + y;
                } else {
                    adjustedX = window.innerWidth / 2 + x;
                    adjustedY = window.innerHeight / 2 + y;
                }

                const finalPosition = adjustPopupPosition(adjustedX, adjustedY);
                showPopup(foundList, finalPosition);
            } else {
                console.warn(`InfoList with id ${infoListId} not found`);
            }
        };

        (window as any).updateInfoListPositionApi = async (infoListId: string) => {
            // ONLY call API if we are in update mode for this specific list
            // This prevents accidental data loss from partial updates
            if (!isUpdateFormOpen || updateFormInfoList?.id.toString() !== infoListId) {
                console.log(`ℹ️ InfoList ${infoListId} moved visually, but not saved because not in Edit Mode.`);
                return;
            }

            const hotspotName = `infolist_${infoListId}`;
            try {
                if (!krpano || !idPath) return;

                const ath = parseFloat(krpano.get(`hotspot[${hotspotName}].ath`) as string);
                const atv = parseFloat(krpano.get(`hotspot[${hotspotName}].atv`) as string);

                // Update text hotspot position as well
                const textHotspotName = `${hotspotName}_text`;
                try {
                    krpano.set(`hotspot[${textHotspotName}].ath`, ath);
                    krpano.set(`hotspot[${textHotspotName}].atv`, atv);
                } catch (e) { /* text might not exist */ }

                // Call API to save new position
                console.log(`📡 Saving InfoList ${infoListId} new position: ath=${ath}, atv=${atv}`);

                // Use full details from the form if available to avoid data loss
                const existingList = updateFormInfoList || infoLists.find(item => item.id.toString() === infoListId);
                if (existingList) {
                    await listService.updateList(infoListId, idPath, {
                        ...existingList,
                        ath: ath,
                        atv: atv
                    });
                    console.log(`✅ InfoList ${infoListId} position saved.`);
                }
            } catch (error) {
                console.error('Error updating info list position:', error);
            }
        };

        return () => {
            delete (window as any).showInfoListPopup;
        };
    }, [infoLists, krpano, showPopup, isUpdateFormOpen, updateFormInfoList, idPath]);

    return (
        <InfoListManagerContext.Provider value={{
            infoLists,
            currentSceneInfoLists,
            isLoading,
            fetchLists,
            addInfoListToScene,
            removeInfoListFromScene,
            updateInfoListInKrpano,
            refreshInfoLists,
            popupInfoList,
            popupPosition,
            isPopupVisible,
            showPopup,
            hidePopup,
            openUpdateForm,
            updateFormInfoList,
            isUpdateFormOpen,
            closeUpdateForm
        }}>
            {children}
        </InfoListManagerContext.Provider>
    );
};

export const useInfoListManager = () => {
    const context = useContext(InfoListManagerContext);
    if (context === undefined) {
        throw new Error('useInfoListManager must be used within a InfoListManagerProvider');
    }
    return context;
};
